import type {
  MicrosoftPrompt,
  Prompt,
  Status,
  TenantPrompt,
} from "@1js/pl-types";
import { isMicrosoftPrompt, isTenantPrompt } from "@1js/pl-utils";
import { useReducer } from "react";
import { type PromptAction } from "./allPromptsLibrary/AllPromptsLibrary.hook.web";

export type PromptPageState = {
  allPrompts: Prompt[];
  init?: boolean;
  status: Status;
  extensionIconMap?: Record<string, string>;
  allWorkplacePrompts: TenantPrompt[];
  allMicrosoftPrompts: MicrosoftPrompt[];
};

export const initialState: PromptPageState = {
  allPrompts: [],
  init: false,
  status: "loading",
  extensionIconMap: {},
  allWorkplacePrompts: [],
  allMicrosoftPrompts: [],
};

export type PromptsPageAction =
  | {
      type: "REQUEST_ALL_PROMPTS_INIT";
    }
  | {
      type: "REQUEST_ALL_PROMPTS_SUCCEED";
      payload: { prompts: Prompt[]; extensionIconMap?: Record<string, string> };
    }
  | {
      type: "REQUEST_ALL_PROMPTS_FAILED";
    }
  | {
      type: "UPDATE_PROMPT";
      payload: { prompt: Prompt; action: PromptAction };
    };

const reducer = (
  state: PromptPageState,
  action: PromptsPageAction
): PromptPageState => {
  switch (action.type) {
    case "REQUEST_ALL_PROMPTS_INIT":
      return {
        ...state,
        status: "loading",
      };
    case "REQUEST_ALL_PROMPTS_SUCCEED":
      return {
        ...state,
        status: "idle",
        init: true,
        allPrompts: action.payload.prompts,
        extensionIconMap: action.payload.extensionIconMap,
        allWorkplacePrompts: action.payload.prompts.filter(isTenantPrompt),
        allMicrosoftPrompts: action.payload.prompts.filter(isMicrosoftPrompt),
      };
    case "REQUEST_ALL_PROMPTS_FAILED":
      return {
        ...state,
        status: "failed",
        init: true,
        allPrompts: [],
        allWorkplacePrompts: [],
        allMicrosoftPrompts: [],
      };
    case "UPDATE_PROMPT": {
      if (action.payload.action === "remove-workgroup") {
        return {
          ...state,
          allPrompts: state.allPrompts.filter(
            (prompt: Prompt) =>
              prompt.PromptId !== action.payload.prompt.PromptId
          ),
        };
      }

      if (action.payload.action === "publish") {
        return {
          ...state,
          allPrompts: [action.payload.prompt, ...state.allPrompts],
        };
      }

      return {
        ...state,
        allPrompts: state.allPrompts.map((prompt: Prompt) =>
          prompt.Id === action.payload.prompt.Id
            ? { ...action.payload.prompt }
            : prompt
        ),
        allWorkplacePrompts: state.allWorkplacePrompts.map((prompt) =>
          prompt.Id === action.payload.prompt.Id
            ? ({ ...action.payload.prompt } as TenantPrompt)
            : prompt
        ),
        allMicrosoftPrompts: state.allMicrosoftPrompts.map((prompt) =>
          prompt.Id === action.payload.prompt.Id
            ? ({ ...action.payload.prompt } as MicrosoftPrompt)
            : prompt
        ),
      };
    }
    default:
      return state;
  }
};

function usePrompts() {
  const [state, dispatch] = useReducer(reducer, initialState);

  return {
    state,
    dispatch,
  };
}

export default usePrompts;
